import {FC, useContext, useState} from 'react';
import {useSetRecoilState} from 'recoil';
import {SubmitHandler, useForm} from 'react-hook-form';

import Box from '@mui/system/Box';
import Stack from '@mui/system/Stack';
import LoadingButton from '@mui/lab/LoadingButton';
import ContactPersons from './ContactPersons';
import ControlledInput from '../../../../../ui/controlled-input/ControlledInput';

import {CompanyService} from '../../../../../api/services/company.service';

import {companiesForMapQueryTrigger, companiesQueryTrigger} from '../../../../../store/company/atoms';

import {validateOrgnOgnip} from '../../../../../shared/validation/ogrnValidation';

import {ICompanyFormInputs, ICompany} from '../../../../../shared/types/company.types';
import {IContactPerson} from '../../../../../shared/types/person.types';
import {EventBusContext} from '../../../../../providers/EventBus';

interface ICompanyForm {
  company: ICompany;
  contactPersons: IContactPerson[];
}

const style = {
  bgcolor: 'background.paper',
  p: 2,
};

const CompanyForm: FC<ICompanyForm> = ({company, contactPersons}) => {
  const triggerCompaniesQuery = useSetRecoilState(companiesQueryTrigger);
  const triggerCompaniesForMapQuery = useSetRecoilState(companiesForMapQueryTrigger);
  const {
    handleSubmit,
    control,
    reset,
    trigger,
    formState: {isDirty},
  } = useForm<ICompanyFormInputs>({
    defaultValues: {
      fullCompanyName: company.fullCompanyName,
      inn: company.inn,
      ogrn: company.ogrn || '',
      description: company.description || '',
      website: company.website || '',
    },
    mode: 'onChange',
  });
  const [loading, setLoading] = useState(false);
  const {bus} = useContext(EventBusContext);

  const onSubmit: SubmitHandler<ICompanyFormInputs> = async (data) => {
    try {
      setLoading(true);
      await CompanyService.update({
        ...company,
        ...data,
        ogrn: data.ogrn || null,
        description: data.description || null,
        website: data.website || null,
      } as ICompany);

      setTimeout(() => {
        setLoading(false);
        triggerCompaniesForMapQuery((v) => v + 1);
        triggerCompaniesQuery((v) => v + 1);
        reset();
      }, 1500);
    } catch (err: any) {
      bus?.emit('error', [err]);

      setLoading(false);
    }
  };

  return (
    <Box sx={style}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack direction="column" spacing={3}>
          <ControlledInput control={control} name="fullCompanyName" disabled label="Полное название компании" />
          <Stack direction="row" spacing={2}>
            <ControlledInput control={control} name="inn" disabled label="ИНН" size="small" />
            <ControlledInput
              control={control}
              name="ogrn"
              rules={{
                pattern: {
                  value: /^(\d{13})?(\d{15})?$/,
                  message: '13 или 15 цифр',
                },
                validate: {
                  validateOgrnOgrnip: (value, formValues) => validateOrgnOgnip(value, formValues.ogrn),
                },
              }}
              disabled={!!company.ogrn}
              label="ОГРН/ОГРНИП"
              size="small"
            />
            <ControlledInput control={control} name="website" label="Сайт компании" size="small" sx={{flexGrow: 2}} />
          </Stack>
          <ControlledInput
            control={control}
            name="description"
            rules={{
              maxLength: {
                value: 400,
                message: 'Максимум 400 символов',
              },
            }}
            label="Комментарий"
            multiline
            rows={4}
            additionalFunc={() => {
              trigger('description');
            }}
          />
          <ContactPersons contactPersons={contactPersons} />
          <Stack direction="row" justifyContent="flex-end">
            <LoadingButton loading={loading} disabled={!isDirty} variant="contained" type="submit">
              Сохранить
            </LoadingButton>
          </Stack>
        </Stack>
      </form>
    </Box>
  );
};

export default CompanyForm;
